import {isFunction} from 'lodash/fp';

import {BasicTreeViewSelectionOptions, TreeViewSelection} from 'filters/features/FilterDialog/types';
import {FilterRange} from 'modules/decisionsNext/api/types';

import {openFilterDialog} from 'filters';
import {useGroupedByLevelTreeViewSelection} from 'filters/features/FilterDialog/sections/TreeViewSection/selections/useGroupedByLevelTreeViewSelection';
import {IndustryNode} from 'modules/decisionsNext/hooks/trees/useIndustryTree';
import {nullIfEmpty} from 'utils/arrayUtils';

import {ConfidenceScoreBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/ConfidenceScoreBlock';
import {
  confidenceScoreOptions,
  filterRangeToConfidenceScore,
} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/ConfidenceScoreBlock/constants';

type IndustryValue = {
  naicsScore: FilterRange[] | null;
  ids: {
    industryIds?: string[];
    industrySubGroupIds?: string[];
    industryGroupIds?: string[];
  };
};

const industryLevels = [
  {
    key: 'industryGroupIds',
    label: 'Group',
  },
  {
    key: 'industrySubGroupIds',
    label: 'Sub-Group',
  },
  {
    key: 'industryIds',
    label: 'Industry',
  },
];

export type IndustryTreeViewSelectionOptions = BasicTreeViewSelectionOptions & {
  disableConfidenceScore?: boolean;
  hideConfidenceScore?: boolean;
};

export const useIndustryTreeViewSelection: TreeViewSelection<
  string,
  IndustryNode,
  IndustryValue,
  IndustryTreeViewSelectionOptions
> = (tree, {value, setValue}, {disabled, disableConfidenceScore, hideConfidenceScore}) => {
  const selection = useGroupedByLevelTreeViewSelection(
    tree,
    {
      value: value.ids,
      setValue: updated =>
        setValue({
          naicsScore: value.naicsScore,
          ids: isFunction(updated) ? updated(value.ids) : updated,
        }),
    },
    {
      entity: 'Industries',
      levels: industryLevels,
      disabled,
    }
  );

  return {
    ...selection,
    additionalFilters: !hideConfidenceScore && (
      <ConfidenceScoreBlock
        isIgnored={disableConfidenceScore}
        naicsScore={value.naicsScore}
        setConfidenceScores={naics =>
          setValue({
            ids: value.ids,
            naicsScore: nullIfEmpty(naics.naicsSourceConfidenceScore),
          })
        }
      />
    ),
    overrideActiveFilterContent: ({sectionId}) => [
      value.naicsScore?.length && {
        id: 'confidence-score',
        label: 'Confidence Score',
        disabled: hideConfidenceScore || disableConfidenceScore,
        chips: value.naicsScore?.map(range => {
          const score = filterRangeToConfidenceScore(range);

          return {
            id: score,
            disabled: hideConfidenceScore || disableConfidenceScore,
            label: confidenceScoreOptions.find(option => option.value === score)?.label || score,
            onClick: () => openFilterDialog(sectionId),
            onDelete: () =>
              setValue({
                ...value,
                naicsScore: nullIfEmpty(value.naicsScore?.filter(r => filterRangeToConfidenceScore(r) !== score)),
              }),
          };
        }),
      },
      selection.selected.length && {
        id: 'industries',
        label: 'Industries',
        disabled: disabled,
        chips: selection.selected.map(id => ({
          id,
          disabled: disabled,
          label: tree.treeMap.get(id)?.title || id,
          onClick: () => openFilterDialog(sectionId),
          onDelete: () => selection.setSelected(selection.selected.filter(k => k !== id)),
        })),
      },
    ],
  };
};
